import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Archive, ArchiveRestore, Trash2, Search, MessageSquare } from 'lucide-react';

export default function ArchivedChats({ isOpen, onClose, archivedChats = [], onUnarchive, onDelete }) {
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  if (!isOpen) return null;

  const filtered = archivedChats.filter((c) =>
    (c.title || c.Title || 'Untitled').toLowerCase().includes(search.toLowerCase())
  );

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4 flex flex-col max-h-[80vh] animate-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Archive size={15} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-800 dark:text-white">Archived Chats</h2>
              <p className="text-xs text-slate-400 dark:text-slate-500">{archivedChats.length} conversation{archivedChats.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search archived chats..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-[#1d57a5] transition-all"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto hide-scrollbar px-3 py-2">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-3">
                <MessageSquare size={24} className="text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                {search ? 'No results found' : 'No archived chats'}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                {search ? 'Try a different search term' : 'Archive a chat from the sidebar to see it here'}
              </p>
            </div>
          ) : (
            filtered.map((chat) => (
              <div
                key={chat.conversationId || chat.ConversationId}
                className="group flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <MessageSquare size={14} className="text-slate-400" />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                    {chat.title || chat.Title || 'Untitled'}
                  </p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 truncate">
                    {chat.useCaseName || 'General'} · {new Date(chat.lastUpdatedAt).toLocaleDateString()}
                  </p>
                </div>

                {/* Actions — visible on hover */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    onClick={() => onUnarchive?.(chat.conversationId || chat.ConversationId)}
                    title="Unarchive"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-[#1d57a5] hover:bg-[#e8edf5] dark:hover:bg-[#1d57a5]/20 transition-colors"
                  >
                    <ArchiveRestore size={14} />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(chat)}
                    title="Delete"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-700 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2 rounded-xl text-sm font-medium bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete Confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-sm mx-4 animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <Trash2 size={16} className="text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-base font-semibold text-slate-800 dark:text-white">Delete Chat</h3>
              </div>
              <button onClick={() => setConfirmDelete(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Are you sure you want to permanently delete{' '}
                <span className="font-semibold text-slate-800 dark:text-white">
                  {confirmDelete.title || confirmDelete.Title || 'Untitled'}
                </span>? This cannot be undone.
              </p>
            </div>
            <div className="flex gap-2 px-5 pb-5">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2 rounded-xl text-sm font-medium bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { onDelete?.(confirmDelete.conversationId || confirmDelete.ConversationId); setConfirmDelete(null); }}
                className="flex-1 px-4 py-2 rounded-xl text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center justify-center gap-1.5"
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
